import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import CampusDashboard from "shared/components/campus";
import HeadOfficeDashboard from "shared/components/headOffice";
import HeadOfficeDashboardSkeleton from "shared/components/headOffice/Skeleton";
import { HeadOfficeThemeProvider } from "shared/components/headOffice/theme/HeadOfficeThemeContext";
import NoDataFound from "shared/components/noDataFound";
import {
  InstallmentsTable,
  PaymentRecordsTable,
  PlanCompareModal,
  PlanHistoryTable,
  ReasonModal,
  ReceiptModal,
} from "shared/components/schoolAnalytics";
import { setSchoolScopeId } from "shared/services/schoolScope";
import useBreadcrumbs from "shared/customHooks/useBreadCrumbs";
import { routeConstant } from "shared/routes/routesConstant";
import useSchoolDetail from "./useSchoolDetail";
import useSchoolInstallments from "./useSchoolInstallments";
import useSchoolPlans, {
  HISTORY_PAGE_SIZE,
  toCampuses,
  toCompareRows,
} from "./useSchoolPlans";
import styles from "./style.module.scss";

/**
 * One school: its reading analytics, then its plans and payments.
 *
 * The dashboard on top is the same one that school sees on aliflaila.app.
 * Which dashboard that is depends on the school: a head office
 * (`is_multi_campus === 1`) reports campus by campus, while a standalone school
 * has no campuses and reports class by class. They are two different screens on
 * two different endpoint families (`school/head-office/*` vs `school/*-v1`), so
 * the flag on the school row decides which one renders. Both call the school
 * API directly, scoped to this school's id — the admin API has no mirror of
 * those endpoints. The scope is registered before either dashboard mounts, so
 * every widget's first request already carries it.
 *
 * Underneath, the money: the school's plan history and payment records, read
 * from the same two routes the school panel's Membership Plans screen uses
 * (see `useSchoolPlans`), and — only when the school pays its plan in
 * installments — that plan's installment schedule (see `useSchoolInstallments`).
 */
function SchoolAnalyticsDetail() {
  const { id } = useParams();
  const schoolId = Number(id);

  // Set during render, not in an effect: the dashboard's widgets fire their
  // requests on mount, which happens before any effect here would run.
  setSchoolScopeId(Number.isFinite(schoolId) ? schoolId : undefined);

  // Clear the scope on the way out so a later screen can't inherit it.
  useEffect(() => () => setSchoolScopeId(undefined), []);

  const { school, loading } = useSchoolDetail(schoolId);
  const name = school?.name || `School #${id}`;
  const isHeadOffice = Number(school?.is_multi_campus) === 1;

  const {
    plans,
    plansLoading,
    plansPage,
    plansTotal,
    setPlansPage,
    payments,
    paymentsLoading,
    paymentsPage,
    paymentsTotal,
    setPaymentsPage,
    receipts,
    rawPlans,
    planReceipts,
    currency,
  } = useSchoolPlans(schoolId);

  const installments = useSchoolInstallments(schoolId);

  useBreadcrumbs(
    {
      title: school?.name ?? routeConstant.schoolAnalyticsDetail.title,
      path: `${routeConstant.schoolAnalytics.path}/${id}`,
    },
    true,
    id
  );

  /* Both open in a dialog rather than a new tab, and they stay separate: the
     eye button shows the receipt image, the row's flag shows why the payment
     was rejected. */
  const [openReceiptId, setOpenReceiptId] = useState<string | null>(null);
  const [openReasonId, setOpenReasonId] = useState<string | null>(null);

  /* The plan table's own two buttons: its details, and the receipt of the
     payment made against it. */
  const [openPlanId, setOpenPlanId] = useState<string | null>(null);
  const [openPlanReceiptId, setOpenPlanReceiptId] = useState<string | null>(
    null
  );

  // Nothing can be fetched before the school type is known — mounting the wrong
  // dashboard would fire a whole set of requests against the wrong endpoints.
  if (loading) {
    return (
      <div className={styles.page}>
        <HeadOfficeDashboardSkeleton />
      </div>
    );
  }

  // The list endpoint is the only source for a single school, so an id that is
  // not in it reports empty rather than sitting on a skeleton forever.
  if (!school) {
    return (
      <div className={styles.page}>
        <NoDataFound />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <HeadOfficeThemeProvider>
        {isHeadOffice ? (
          <HeadOfficeDashboard schoolName={name} />
        ) : (
          <CampusDashboard schoolName={name} subtitle="School Dashboard" />
        )}
      </HeadOfficeThemeProvider>

      <PlanHistoryTable
        plans={plans}
        loading={plansLoading}
        page={plansPage}
        pageSize={HISTORY_PAGE_SIZE}
        total={plansTotal}
        onPageChange={setPlansPage}
        currency={currency}
        onViewPlan={(planId) => setOpenPlanId(String(planId))}
        onViewReceipt={(planId) => setOpenPlanReceiptId(String(planId))}
      />

      {installments.plan && (
        <InstallmentsTable
          installments={installments.rows}
          loading={installments.loading}
          meta={installments.meta}
          currency={installments.currency}
        />
      )}

      <PaymentRecordsTable
        payments={payments}
        loading={paymentsLoading}
        page={paymentsPage}
        pageSize={HISTORY_PAGE_SIZE}
        total={paymentsTotal}
        onPageChange={setPaymentsPage}
        currency={currency}
        onViewReceipt={setOpenReceiptId}
        onViewReason={setOpenReasonId}
        emptyLabel={
          plans.length
            ? "No payments recorded against this school's plans."
            : "This school has never held a plan, so there are no payments."
        }
      />

      <ReceiptModal
        open={Boolean(openReceiptId)}
        receipt={openReceiptId ? receipts[openReceiptId] : undefined}
        onClose={() => setOpenReceiptId(null)}
      />

      <ReasonModal
        open={Boolean(openReasonId)}
        receipt={openReasonId ? receipts[openReasonId] : undefined}
        onClose={() => setOpenReasonId(null)}
      />

      <ReceiptModal
        open={Boolean(openPlanReceiptId)}
        receipt={
          openPlanReceiptId ? planReceipts[openPlanReceiptId] : undefined
        }
        onClose={() => setOpenPlanReceiptId(null)}
        title="Plan receipt"
      />

      <PlanCompareModal
        open={Boolean(openPlanId)}
        onClose={() => setOpenPlanId(null)}
        subtitle={openPlanId ? rawPlans[openPlanId]?.name : undefined}
        rows={openPlanId ? toCompareRows(rawPlans[openPlanId]) : []}
        campuses={openPlanId ? toCampuses(rawPlans[openPlanId]) : undefined}
      />
    </div>
  );
}

export default SchoolAnalyticsDetail;
